import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, Alert, Checkbox } from 'antd';
import {
  EnvelopeIcon,
  LockClosedIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from 'hooks/useAuth';
import { ROUTES } from 'config/routes';
import { formRules } from 'utils/validators';
import styles from './Login.module.css';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearAuthError } = useAuth();
  const [form] = Form.useForm();

  const from = location.state?.from?.pathname || ROUTES.DASHBOARD;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    return () => {
      clearAuthError();
    };
  }, [clearAuthError]);

  const handleSubmit = async (values: LoginFormData) => {
    try {
      await login(values.email, values.password);
    } catch (error) {

    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginBg}>
        {isLoading && (
          <div className={styles.loginLoader}>
            <div className={styles.loginLoaderText}>
              <span>Push</span>
              <span>Notify</span>
            </div>
          </div>
        )}
        <span className={`${styles.blob} ${styles.blob1}`} />
        <span className={`${styles.blob} ${styles.blob2}`} />
        <span className={`${styles.blob} ${styles.blob3}`} />
      </div>

      <div className={styles.loginContainer}>
        <Card className={styles.loginCard}>
          <div className={styles.loginHeader}>
            <div className={styles.loginLogo}><BellAlertIcon /></div>
            <h1 className={styles.loginTitle}>Push Notify</h1>
            <p className={styles.loginSubtitle}>Войдите в свой аккаунт</p>
          </div>

          {error && (
            <Alert
              message="Ошибка входа"
              description={error}
              type="error"
              showIcon
              closable
              onClose={clearAuthError}
              className={styles.loginAlert}
            />
          )}

          <Form
            form={form}
            name="login"
            onFinish={handleSubmit}
            autoComplete="off"
            layout="vertical"
            requiredMark={false}
            initialValues={{ remember: true }}
          >
            <Form.Item name="email" rules={formRules.email}>
              <Input
                prefix={<span className={styles.inputIcon}><EnvelopeIcon /></span>}
                placeholder="Электронная почта"
                size="large"
                autoFocus
              />
            </Form.Item>

            <Form.Item name="password" rules={formRules.password}>
              <Input.Password
                prefix={<span className={styles.inputIcon}><LockClosedIcon /></span>}
                placeholder="Пароль"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <div className={styles.loginOptions}>
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>Запомнить меня</Checkbox>
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={false}
                disabled={isLoading}
              >
                Войти
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default Login;